/**
 * ItcChartService v2.0 — Web port
 * ECharts-based. Same API surface as the RN original.
 * MarketDataService calls updateTickPrice() directly.
 */
import * as echarts from 'echarts';
import { EMA, SMA, BollingerBands, MACD, RSI, CCI, Stochastic, ROC, ADX } from 'technicalindicators';
import { store } from '../../redux/store';
import { setChartLoading } from '../../redux/features/platformSlice';
import MarketDataService from '../market/MarketDataService';
import platformService from '../platform/platformService';

const MAX_CANDLES = 500;
const REDRAW_THROTTLE = 100;
const INIT_ZOOM = 90;
const TARGET_CANDLES = 200;
const TARGET_VISIBLE_CANDLES = 90;

const MS_MAP = {
  M1: 60000,
  M2: 120000,
  M3: 180000,
  M4: 240000,
  M5: 300000,
  M6: 360000,
  M10: 600000,
  M12: 720000,
  M15: 900000,
  M20: 1200000,
  M30: 1800000,
  H1: 3600000,
  H2: 7200000,
  H3: 10800000,
  H4: 14400000,
  H6: 21600000,
  H8: 28800000,
  H12: 43200000,
  D1: 86400000,
  W1: 604800000,
  MN1: 2592000000,
};

function getIntervalMs(interval) { return MS_MAP[interval] || 60000; }

function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

function fmtPrice(v, d = 5) {
  if (v > 10) return +v.toFixed(2);
  if (v % 1 !== 0) { const s = String(v).split('.'); if (s[1]?.length > 5) return +v.toFixed(5); }
  return v;
}

class ItcChartService {
  chart_ready = false;
  symbol = null;
  interval = 'M1';
  chartType = 'candlestick';
  zoomLevel = INIT_ZOOM;

  // theme colors (updated from outside)
  themeColors = null;

  // candle buffers
  dates = [];
  candlestickData = [];
  lineData = [];
  timestamps = [];

  // dynamic lookback & settings properties
  showGrid = true;
  showBidLine = true;
  showOrderLines = true;
  timeOffset = 0;
  _loadingOlderHistory = false;

  // current candle state
  _currentOpen = 0;
  _currentHigh = 0;
  _currentLow = 0;
  _currentClose = 0;
  _currentBucket = null;

  buy_price = 0;
  sell_price = 0;

  // chart
  _chartEl = null;
  _chartInstance = null;
  _setChartOptions = null;
  _historyToken = 0;
  _redrawTimer = null;
  _redrawInProgress = false;

  // marklines & markpoints
  _markLines = new Map();
  _markPoints = [];

  // indicators
  chartIndicator = {};

  // constructor() {}

  // ─── Init ──────────────────────────────────────────────────────────────────
  init(chartEl, setChartOptions, symbol, chartType = 'candlestick') {
    // Cancel any pending redraws from a previous session
    if (this._redrawTimer) { clearTimeout(this._redrawTimer); this._redrawTimer = null; }

    this.chart_ready = false;
    this.dates = []; this.candlestickData = []; this.lineData = []; this.timestamps = [];
    this._currentBucket = null; this._markPoints = []; this._markLines.clear();
    this.chartIndicator = {}; // reset indicators on every new symbol/init
    this._redrawInProgress = false;

    store.dispatch(setChartLoading(true));
    this._chartEl = chartEl;
    this._setChartOptions = setChartOptions;
    this.symbol = symbol;
    this.chartType = chartType;

    // If the element changed (e.g. remount) dispose the old instance first
    if (chartEl && this._chartInstance) {
      const prevEl = this._chartInstance.getDom?.();
      if (prevEl && prevEl !== chartEl) {
        this._chartInstance.dispose();
        this._chartInstance = null;
      }
    }

    // Create ECharts instance if needed
    if (chartEl && !this._chartInstance) {
      this._chartInstance = echarts.init(chartEl, null, { renderer: 'canvas' });
      this._chartInstance.on('datazoom', (params) => {
        this._handleDataZoom(params);
      });
    }

    // Apply a clean slate with notMerge:true so stale series/grids are cleared
    this._applyInitialOptions(true);
    this._fetchHistory();
  }

  updateChartEl(el) {
    if (!el || el === this._chartEl) return;
    this._chartEl = el;
    if (this._chartInstance) { this._chartInstance.dispose(); }
    this._chartInstance = echarts.init(el, null, { renderer: 'canvas' });
    if (this.chart_ready) this._forceRedraw();
  }

  resize() { this._chartInstance?.resize(); }

  zoomIn() {
    if (!this._chartInstance) return;
    const opts = this._chartInstance.getOption();
    const dz = opts?.dataZoom?.[0];
    if (!dz) return;
    const currentStart = dz.start;
    const currentEnd = dz.end;
    const range = currentEnd - currentStart;
    if (range <= 5) return; // limit max zoom
    const newStart = Math.min(95, currentStart + range * 0.1);
    this.zoomLevel = newStart;
    this._chartInstance.setOption({
      dataZoom: [{ start: newStart, end: currentEnd }]
    });
  }

  zoomOut() {
    if (!this._chartInstance) return;
    const opts = this._chartInstance.getOption();
    const dz = opts?.dataZoom?.[0];
    if (!dz) return;
    const currentStart = dz.start;
    const currentEnd = dz.end;
    const range = currentEnd - currentStart;
    const newStart = Math.max(0, currentStart - range * 0.1);
    this.zoomLevel = newStart;
    this._chartInstance.setOption({
      dataZoom: [{ start: newStart, end: currentEnd }]
    });
  }

  toggleGrid(visible) {
    this.showGrid = visible !== undefined ? visible : !this.showGrid;
    this._forceRedraw();
  }

  toggleBidLine(visible) {
    this.showBidLine = visible !== undefined ? visible : !this.showBidLine;
    this._forceRedraw();
  }

  toggleOrderLines(visible) {
    this.showOrderLines = visible !== undefined ? visible : !this.showOrderLines;
    this._forceRedraw();
  }

  // ─── Fetch history from API ────────────────────────────────────────────────
  async _fetchHistory() {
    const token = ++this._historyToken;
    try {
      const state = store.getState();
      const user = state.platform.user;
      const group = user?.group_id?.group_name || '';

      const ms = getIntervalMs(this.interval);
      const to = Date.now();

      // Calculate a dynamic lookback that seamlessly handles weekends and closed markets
      let lookbackMs = TARGET_CANDLES * ms;
      const isSubHour = /^M\d+$/i.test(this.interval);
      const minLookback = (isSubHour ? 3 : 10) * 24 * 3600 * 1000; // 3 days or 10 days

      if (lookbackMs < minLookback) {
        lookbackMs = minLookback;
      }

      const from = to - lookbackMs;

      const res = await platformService.getChartHistory(
        this.symbol,
        this.interval,
        from,
        to,
        group
      );

      if (token !== this._historyToken) return;

      const candles = this._extractHistoryCandles(res);
      if (candles.length > 0) {
        this._loadHistory(candles);
      } else {
        console.warn('[ItcChartService] No history data returned from API');
      }

      this.chart_ready = true;
      const latestPrice = MarketDataService.getPrice(this.symbol);
      if (latestPrice) this.updateTickPrice(latestPrice);
      setTimeout(() => {
        this._forceRedraw();
        setTimeout(() => this._chartInstance?.resize(), 200);
      }, 80);
    } catch (err) {
      console.error('[ItcChartService] _fetchHistory error:', err);
      store.dispatch(setChartLoading(false));
    } finally {
      if (token === this._historyToken) store.dispatch(setChartLoading(false));
    }
  }


  _loadHistory(data) {
    this.dates = []; this.candlestickData = []; this.lineData = []; this.timestamps = [];
    this._currentBucket = null;
    this.timeOffset = 0;

    if (data.length > 0) {
      for (const item of data) {
        const candle = this._normaliseCandle(item);
        if (!candle) continue;

        this.dates.push(this._formatDate(candle.timestamp));
        this.candlestickData.push([candle.open, candle.close, candle.low, candle.high]);
        this.lineData.push(candle.close);
        this.timestamps.push(candle.timestamp);
      }
    }

    this._capBuffers();
    this.zoomLevel = this._calcInitialZoomStart();

    this._syncCurrentCandleState();
  }

  _extractHistoryCandles(res) {
    const payload = res?.data ?? res;
    const data = payload?.data ?? payload;
    const candles = Array.isArray(data?.candles)
      ? data.candles
      : Array.isArray(data)
        ? data
        : [];

    return candles
      .map(item => this._normaliseCandle(item))
      .filter(Boolean)
      .sort((a, b) => a.timestamp - b.timestamp);
  }

  _normaliseCandle(item) {
    if (!item) return null;

    let timestamp = Number(item.timestamp ?? item.time ?? item.at);
    if (!Number.isFinite(timestamp) || timestamp <= 0) return null;
    if (timestamp < 100000000000) timestamp *= 1000;

    const open = parseFloat(item.open);
    const high = parseFloat(item.high);
    const low = parseFloat(item.low);
    const close = parseFloat(item.close);

    if (![open, high, low, close].every(Number.isFinite)) return null;

    return { ...item, timestamp, open, high, low, close };
  }

  _syncCurrentCandleState(index = this.candlestickData.length - 1) {
    if (index < 0 || !this.candlestickData[index]) {
      this._currentBucket = null;
      return;
    }

    const intervalMs = getIntervalMs(this.interval);
    const candle = this.candlestickData[index];
    this._currentOpen = candle[0];
    this._currentClose = candle[1];
    this._currentLow = candle[2];
    this._currentHigh = candle[3];
    this._currentBucket = Math.floor(this.timestamps[index] / intervalMs);
  }

  _findCandleIndexByBucket(bucket, intervalMs) {
    for (let i = this.timestamps.length - 1; i >= 0; i--) {
      if (Math.floor(this.timestamps[i] / intervalMs) === bucket) return i;
      if (Math.floor(this.timestamps[i] / intervalMs) < bucket) break;
    }
    return -1;
  }

  // ─── Live tick ─────────────────────────────────────────────────────────────
  _capBuffers() {
    if (this.dates.length > MAX_CANDLES) {
      this.dates = this.dates.slice(-MAX_CANDLES);
      this.candlestickData = this.candlestickData.slice(-MAX_CANDLES);
      this.lineData = this.lineData.slice(-MAX_CANDLES);
      if (this.timestamps) {
        this.timestamps = this.timestamps.slice(-MAX_CANDLES);
      }
    }
  }

  updateTickPrice(price) {
    if (!price || !this.symbol) return;
    const bid = parseFloat(price.bid);
    const ask = parseFloat(price.ask);
    let timestamp = Number(price.timestamp ?? price.time ?? price.at ?? Date.now());
    if (timestamp < 100000000000) timestamp *= 1000;
    if (!bid || !ask) return;
    this._processTick({ bid, ask, timestamp });
  }

  _processTick({ bid, ask, timestamp }) {
    if (!this.chart_ready) return;
    if (!bid || !ask || !timestamp) return;

    const ts = timestamp;
    const intervalMs = getIntervalMs(this.interval);
    const bucket = Math.floor(ts / intervalMs);

    const existingIndex = this._findCandleIndexByBucket(bucket, intervalMs);

    if (existingIndex >= 0) {
      const candle = this.candlestickData[existingIndex];
      const open = candle[0];
      const high = Math.max(candle[3], bid);
      const low = Math.min(candle[2], bid);
      this.candlestickData[existingIndex] = [open, bid, low, high];
      this.lineData[existingIndex] = bid;
      this._syncCurrentCandleState();
    } else if (this.timestamps.length && bucket < Math.floor(this.timestamps[0] / intervalMs)) {
      // Ignore stale ticks older than the loaded history window.
      this.buy_price = ask;
      this.sell_price = bid;
      return;
    } else if (this._currentBucket === null || bucket > this._currentBucket || !this.candlestickData.length) {
      this._currentBucket = bucket;
      this._currentOpen = bid; this._currentClose = bid;
      this._currentHigh = bid; this._currentLow = bid;
      const bucketStartTs = bucket * intervalMs;
      this.dates.push(this._formatDate(bucketStartTs));
      this.candlestickData.push([this._currentOpen, this._currentClose, this._currentLow, this._currentHigh]);
      this.lineData.push(bid);
      this.timestamps.push(bucketStartTs);
      this._capBuffers();
      this._updateAllIndicators().catch(() => { });
    }
    this.buy_price = ask;
    this.sell_price = bid;
    this._scheduleRedraw();
  }

  _formatDate(timestamp) {
    const d = new Date(timestamp);
    const ms = getIntervalMs(this.interval);
    if (ms >= 86400000 * 30) return `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    if (ms >= 86400000) return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    if (ms >= 3600000) return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
  }

  // ─── Interval / Type changes ───────────────────────────────────────────────
  candleSizeInterval(interval) {
    if (this.interval === interval) return;
    this.interval = interval;
    this.reset();
    store.dispatch(setChartLoading(true));
    this._fetchHistory();
  }

  async changeChartType(type) {
    this.chartType = type;
    this._forceRedraw();
  }

  reset() {
    this.chart_ready = false;
    this.dates = []; this.candlestickData = []; this.lineData = []; this.timestamps = [];
    this._currentBucket = null; this._markPoints = []; this._markLines.clear();
    this.chartIndicator = {}; // clear stale indicator registry
    this._redrawInProgress = false;
    if (this._redrawTimer) { clearTimeout(this._redrawTimer); this._redrawTimer = null; }
  }

  // ─── MarkLine API (same as RN) ─────────────────────────────────────────────
  addMarkLine(id, opts = {}) {
    const yAxis = parseFloat(opts.yAxis);
    if (!Number.isFinite(yAxis)) return;

    this._markLines.set(id, {
      id, name: opts.name ?? id, yAxis,
      color: opts.color ?? '#aaaaaa', lineType: opts.lineType ?? 'dashed',
      width: opts.width ?? 1, opacity: opts.opacity ?? 0.58,
      labelPosition: opts.labelPosition ?? 'end',
      labelDistance: opts.labelDistance ?? 8,
      labelColor: opts.labelColor ?? '#ffffff',
    });
    this._scheduleRedraw();
  }
  updateMarkLine(id, updates = {}) {
    const ex = this._markLines.get(id); if (!ex) return;
    this._markLines.set(id, { ...ex, ...updates });
    this._scheduleRedraw();
  }
  removeMarkLine(id) { this._markLines.delete(id); this._scheduleRedraw(); }
  clearMarkLines() { this._markLines.clear(); this._scheduleRedraw(); }

  // ─── MarkPoint API ─────────────────────────────────────────────────────────
  addMarkPoint({ id, price, amount, timestamp, type, label }) {
    if (!price) return;
    if (!this.dates.length) { setTimeout(() => this.addMarkPoint({ id, price, amount, timestamp, type, label }), 1000); return; }
    const ts = timestamp ? (typeof timestamp === 'string' ? new Date(timestamp).getTime() : timestamp > 9_999_999_999 ? timestamp : timestamp * 1000) : Date.now();
    const intervalMs = getIntervalMs(this.interval);
    const bucket = Math.floor(ts / intervalMs);
    const bucketTs = bucket * intervalMs;
    // find closest date
    let date = this.dates[this.dates.length - 1];
    const fmtTarget = this._formatDate(bucketTs);
    if (this.dates.includes(fmtTarget)) date = fmtTarget;
    this._markPoints = this._markPoints.filter(p => p.id !== id);
    this._markPoints.push({ id, amount, price, date, type, label, _ts: ts });
    this._scheduleRedraw();
  }
  removeMarkPoint(id) { this._markPoints = this._markPoints.filter(p => p.id !== id); this._scheduleRedraw(); }
  clearMarkPoints() { this._markPoints = []; this._scheduleRedraw(); }

  _formatMarkLineValue(value, digits) {
    const num = parseFloat(value);
    return Number.isFinite(num) ? num.toFixed(digits) : '';
  }

  // ─── ECharts option building ───────────────────────────────────────────────
  _getColors() {
    const t = this.themeColors;
    if (t) return {
      bg: t.chartBg, buyCandle: t.buyCandle, sellCandle: t.selCandle,
      marklineBuy: t.marklineBuy, marklineSell: t.marklineSell, marklineOpen: t.marklineOpen,
      marklineSL: t.marklineSL, marklineTP: t.marklineTP,
      axisLabel: t.axisLabel, axisPointer: t.surface || '#1a2540', splitLine: t.splitLine,
      lineChart: t.lineChart, lineChartA1: t.lineChartA1, lineChartA2: t.lineChartA2,
      tooltipBg: t.card || '#162033', tooltipBorder: t.border || '#1e2d47', tooltipText: t.text || '#f1f5f9',
    };
    return {
      bg: '#0a0e1a', buyCandle: '#388ee3', sellCandle: '#eb434e',
      marklineBuy: '#388ee3', marklineSell: '#eb434e', marklineOpen: '#aaaaaa',
      marklineSL: '#E55604', marklineTP: '#bb86fc',
      axisLabel: '#888899', axisPointer: '#1a2540', splitLine: '#1e2d47',
      lineChart: '#388ee3', lineChartA1: 'rgba(56,142,227,0.20)', lineChartA2: 'rgba(56,142,227,0)',
      tooltipBg: '#162033', tooltipBorder: '#1e2d47', tooltipText: '#f1f5f9',
    };
  }

  _datesWithGap() { return this.dates; }

  _calcInitialZoomStart() {
    const count = this.candlestickData.length;
    if (count <= TARGET_VISIBLE_CANDLES) return 0;
    return Math.max(0, 100 - (TARGET_VISIBLE_CANDLES / count) * 100);
  }

  _getMarkLineLabelPixel(yAxis) {
    if (!this._chartInstance || !Number.isFinite(yAxis)) return null;
    try {
      const pixel = this._chartInstance.convertToPixel({ yAxisIndex: 0 }, yAxis);
      return Number.isFinite(pixel) ? pixel : null;
    } catch {
      return null;
    }
  }

  _stackMarkLineLabels(rows) {
    const minLabelGap = 22;
    const labelStep = 18;
    const stackable = rows
      .map((row, index) => ({ index, y: this._getMarkLineLabelPixel(row.yAxis) }))
      .filter(item => item.y !== null)
      .sort((a, b) => a.y - b.y);

    if (stackable.length < 2) return rows;

    const clusters = [];
    for (const item of stackable) {
      const cluster = clusters[clusters.length - 1];
      if (!cluster || Math.abs(item.y - cluster[cluster.length - 1].y) >= minLabelGap) {
        clusters.push([item]);
      } else {
        cluster.push(item);
      }
    }

    for (const cluster of clusters) {
      if (cluster.length < 2) continue;

      const levels = [];
      for (const item of cluster) {
        const level = levels.find(group => Math.abs(group.y - item.y) < 1);
        if (level) {
          level.items.push(item);
        } else {
          levels.push({ y: item.y, items: [item] });
        }
      }

      if (levels.length < 2) continue;

      const center = (levels.length - 1) / 2;
      const centerY = levels.reduce((sum, level) => sum + level.y, 0) / levels.length;
      levels.forEach((level, i) => {
        const targetY = centerY + ((i - center) * labelStep);
        const offsetY = Math.round(targetY - level.y);
        level.items.forEach(item => {
          const row = rows[item.index];
          row.label = { ...row.label, offset: [0, offsetY] };
        });
      });
    }

    return rows;
  }

  _buildMarkLineData() {
    const c = this._getColors();
    const meta = MarketDataService.getSymbolMeta(this.symbol);
    const digits = meta?.digits ?? 2;
    const rows = [];
    if (this.sell_price && this.showBidLine) {
      rows.push({
        name: 'Bid', yAxis: this.sell_price,
        lineStyle: { color: c.marklineSell, type: 'solid', width: 1, opacity: 0.72 },
        label: {
          color: '#fff', backgroundColor: c.marklineSell, position: 'end', distance: 4, width: 50, padding: [2, 4, 4, 10],
          formatter: (p) => this._formatMarkLineValue(p.value, digits)
        }
      });
    }
    if (this.showOrderLines) {
      for (const ml of this._markLines.values()) {
        rows.push({
          name: ml.name, yAxis: ml.yAxis,
          lineStyle: { color: ml.color, type: ml.lineType, width: ml.width, opacity: ml.opacity },
          label: {
            color: ml.labelColor, backgroundColor: ml.color, position: ml.labelPosition,
            distance: ml.labelDistance, padding: [4, 16, 4, 2],
            borderRadius: 4, fontSize: 10, fontWeight: 700,
            formatter: (p) => {
              const value = this._formatMarkLineValue(p.value, digits);
              return p.name ? `${p.name} (${value})` : value;
            }
          }
        });
      }
    }
    return this._stackMarkLineLabels(rows);
  }

  _buildMarkPointData() {
    if (!this.showOrderLines) return [];
    const meta = MarketDataService.getSymbolMeta(this.symbol);
    const digits = meta?.digits ?? 2;
    return this._markPoints.filter(p => p.price && this.dates.includes(p.date)).map(p => {
      const isBuy = p.type === 'buy';
      return {
        xAxis: p.date, yAxis: p.price, value: p.label ?? p.amount,
        symbolSize: [48, 40],
        itemStyle: { color: isBuy ? '#388ee3' : '#eb434e' },
        label: {
          show: true, color: '#fff', fontSize: 8, fontWeight: 'bold', position: isBuy ? 'top' : 'bottom', offset: [0, isBuy ? 30 : -30],
          formatter: (param) => typeof param.value === 'number' ? param.value.toFixed(digits) : param.value
        },
      };
    });
  }

  _applyInitialOptions(notMerge = false) {
    const c = this._getColors();
    const splitLine = { show: this.showGrid, lineStyle: { color: c.splitLine, type: 'solid', width: 1, opacity: 0.5 } };
    const meta = MarketDataService.getSymbolMeta(this.symbol);
    const digits = meta?.digits ?? 2;

    const axisLabel = {
      fontSize: '10px',
      color: c.axisLabel,
      fontFamily: 'JetBrains Mono,Fira Code,monospace',
      formatter: (val) => typeof val === 'number' ? val.toFixed(digits) : val
    };
    const pointerLabel = {
      shadowBlur: 0,
      margin: 0,
      backgroundColor: c.axisPointer,
      color: '#f7f8f9',
      formatter: (params) => typeof params.value === 'number' ? params.value.toFixed(digits) : params.value
    };
    const pointerLine = { color: c.splitLine, type: 'solid' };

    const opts = {
      animation: false, backgroundColor: c.bg,
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross', lineStyle: { color: '#4f576d44' } },
        backgroundColor: c.tooltipBg,
        borderColor: c.tooltipBorder,
        textStyle: { color: c.tooltipText, fontSize: 11, fontFamily: 'JetBrains Mono,monospace' },
        formatter: (params) => {
          const meta = MarketDataService.getSymbolMeta(this.symbol);
          const digits = meta?.digits ?? 2;
          let res = `<div style="font-weight:700;margin-bottom:4px;border-bottom:1px solid ${c.splitLine}66;padding-bottom:4px">${params[0].axisValue}</div>`;
          params.forEach((p) => {
            if (p.seriesType === 'candlestick') {
              const d = p.data; // [open, close, low, high]
              if (!d) return;
              const open = parseFloat(d[1]);
              const close = parseFloat(d[2]);
              const low = parseFloat(d[3]);
              const high = parseFloat(d[4]);
              // const color = close >= open ? '#26a69a' : '#ef5350';

              const row = (label, val) => `
                <div style="display:flex;justify-content:space-between;gap:12px;font-size:10px;margin-bottom:2px">
                  <span style="color:${c.axisLabel}">${label}</span>
                  <span style="font-family:monospace;font-weight:600">${val.toFixed(digits)}</span>
                </div>`;

              res += row('Open', open);
              res += row('High', high);
              res += row('Low', low);
              res += row('Close', close);
            } else if (p.seriesType === 'line' && p.seriesName !== 'Volume' && p.data !== undefined) {
              const val = Array.isArray(p.data) ? p.data[1] : p.data;
              if (val === undefined || val === null) return;
              res += `
                <div style="display:flex;justify-content:space-between;gap:12px;font-size:10px;margin-top:2px">
                  <span style="color:${p.color}">${p.seriesName || 'Value'}</span>
                  <span style="font-family:monospace;font-weight:600">${parseFloat(val).toFixed(digits)}</span>
                </div>`;
            }
          });
          return res;
        }
      },
      axisPointer: { link: [{ xAxisIndex: 'all' }] },
      grid: [{ left: '0', right: '70px', top: '8px', bottom: '0', containLabel: true, borderWidth: 0 }],
      xAxis: [{
        type: 'category', data: this._datesWithGap(), gridIndex: 0,
        axisLine: { lineStyle: { width: 0 } }, axisTick: { show: false },
        axisLabel: { ...axisLabel, margin: 8 },
        axisPointer: { show: true, lineStyle: pointerLine, label: pointerLabel },
        splitLine: { show: false },
      }],
      yAxis: [{
        scale: true, gridIndex: 0, position: 'right', boundaryGap: [0, 0],
        axisLabel: { ...axisLabel, margin: 12 },
        axisTick: { show: false }, axisLine: { show: false },
        axisPointer: { show: true, lineStyle: pointerLine, label: pointerLabel },
        splitLine,
      }, {
        scale: true, gridIndex: 0, position: 'left', show: false,
        boundaryGap: ['0%', '70%'], axisTick: { show: false }, axisLine: { show: false },
        splitLine: { show: false }, axisLabel: { show: false }, axisPointer: { show: false },
      }],
      dataZoom: [{ type: 'inside', xAxisIndex: [0], start: this.zoomLevel, end: 100, moveOnMouseMove: true, moveOnMouseWheel: true }],
      series: [
        {
          type: 'candlestick', data: [], xAxisIndex: 0, yAxisIndex: 0, animation: false, large: true,
          barMaxWidth: 10,
          barMinWidth: 2,
          itemStyle: { color: c.buyCandle, color0: c.sellCandle, borderColor: c.buyCandle, borderColor0: c.sellCandle, opacity: this.chartType === 'candlestick' ? 1 : 0 },
          markPoint: { tooltip: { show: false }, data: [], symbolSize: [36, 36], symbolKeepAspect: true },
          markLine: { symbol: ['none', 'none'], precision: 8, data: [], lineStyle: { width: 1, type: 'solid' }, label: { show: true, width: 80, fontSize: '10px', padding: [3, 0, 3, 0], borderRadius: 4 } }
        },
        {
          type: 'line', data: [], xAxisIndex: 0, yAxisIndex: 0, symbolSize: 0, animation: false,
          lineStyle: { color: c.lineChart, opacity: this.chartType === 'candlestick' ? 0 : 1 },
          areaStyle: { opacity: this.chartType === 'area' ? 1 : 0, origin: 'start', color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: c.lineChartA1 }, { offset: 1, color: c.lineChartA2 }], global: false } }
        },
        { name: 'Volume', type: 'bar', data: [], yAxisIndex: 1, xAxisIndex: 0, itemStyle: { opacity: 0 } },
      ],
    };
    this._applyOptions(opts, notMerge);
  }

  _applyOptions(opts, notMerge = false) {
    if (this._setChartOptions) this._setChartOptions(opts);
    if (this._chartInstance) this._chartInstance.setOption(opts, { notMerge, lazyUpdate: false });
  }

  _scheduleRedraw() {
    if (!this.chart_ready) return;
    if (this._redrawTimer) return;
    this._redrawTimer = setTimeout(async () => {
      this._redrawTimer = null;
      await this._reloadChart();
    }, REDRAW_THROTTLE);
  }

  async _reloadChart() {
    if (this._redrawInProgress) return;
    this._redrawInProgress = true;
    try {
      const c = this._getColors();
      const isCandle = this.chartType === 'candlestick';
      const isArea = this.chartType === 'area';

      const currentOpts = this._chartInstance?.getOption();
      const xAxisCount = currentOpts?.xAxis?.length || 1;
      const datesWithGap = this._datesWithGap();

      const upd = {
        backgroundColor: c.bg,
        xAxis: Array.from({ length: xAxisCount }, () => ({ data: datesWithGap })),
        series: [{
          data: this.candlestickData,
          barMaxWidth: 10,
          barMinWidth: 2,
          itemStyle: { color: c.buyCandle, color0: c.sellCandle, borderColor: c.buyCandle, borderColor0: c.sellCandle, opacity: isCandle ? 1 : 0 },
          markPoint: { data: this._buildMarkPointData() },
          markLine: { data: this._buildMarkLineData() },
        }, {
          data: this.lineData,
          lineStyle: { color: c.lineChart, opacity: isCandle ? 0 : 1 },
          areaStyle: { opacity: isArea ? 1 : 0, origin: 'start', color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: c.lineChartA1 }, { offset: 1, color: c.lineChartA2 }], global: false } },
        }],
      };
      this._applyOptions(upd);
    } finally { this._redrawInProgress = false; }
  }

  _forceRedraw() {
    this._applyInitialOptions(true); // notMerge:true — wipes stale indicator series/grids
    if (this.chart_ready) {
      setTimeout(() => this._reloadChart(), 50);
    }
  }

  // ─── Indicators ────────────────────────────────────────────────────────────
  _genId() { return Date.now() + Math.floor(Math.random() * 9999); }

  async createIndicator(type, data) {
    const m = {
      EMA: () => this.addEMA(data), SMA: () => this.addSMA(data), BBANDS: () => this.addBBANDS(data),
      MACD: () => this.addMACD(data), SO: () => this.addSO(data), RSI: () => this.addRSI(data),
      CCI: () => this.addCCI(data), ROC: () => this.addROC(data), ADX: () => this.addADX(data)
    };
    return m[type]?.() ?? {};
  }

  _generateEmptyPrefix(indLen, totalLen) { return Array(Math.max(0, totalLen - indLen)).fill(0); }
  _fmtInd(data) { return data.map(d => fmtPrice(parseFloat(d))); }

  _getAxisIndices(opts, gridIndex) {
    return {
      xi: opts.xAxis.findIndex(x => x.gridIndex === gridIndex),
      yi: opts.yAxis.findIndex(y => y.gridIndex === gridIndex)
    };
  }

  async _getOptions() { return this._chartInstance?.getOption() ?? null; }
  async _getSeriesLength() { const o = await this._getOptions(); return o?.series?.length ?? 3; }

  async _addIndicatorSeries(data, color, thickness, title, gridIndex = 0) {
    const opts = await this._getOptions(); if (!opts) return;
    const prefix = this._generateEmptyPrefix(data.length, this.lineData.length);
    const payload = prefix.concat(this._fmtInd(data));
    const { xi, yi } = this._getAxisIndices(opts, gridIndex);
    opts.series.push({
      name: title, type: 'line', xAxisIndex: xi === -1 ? 0 : xi, yAxisIndex: yi === -1 ? 0 : yi, symbolSize: 0, data: payload, smooth: false,
      lineStyle: { color, width: thickness }, itemStyle: { color }, areaStyle: { opacity: 0 }, markLine: { symbol: ['none', 'none'], data: [] }
    });
    this._applyOptions(opts);
  }

  async _addSubChart() {
    const opts = await this._getOptions(); if (!opts) return 0;
    const c = this._getColors();
    const gi = opts.grid.length;
    opts.grid.push({ left: '0', right: '70px', top: '75%', height: '23%', containLabel: true, borderWidth: 0, show: true });
    opts.xAxis.push({ type: 'category', data: this._datesWithGap(), gridIndex: gi, axisLabel: { show: false }, axisTick: { show: false }, axisPointer: { show: true, lineStyle: { color: c.splitLine, type: 'solid' }, label: { shadowBlur: 0, backgroundColor: c.axisPointer, color: '#f7f8f9' } }, splitLine: { show: this.showGrid, lineStyle: { color: c.splitLine } } });
    opts.yAxis.push({ scale: true, gridIndex: gi, position: 'right', boundaryGap: [0, 0], axisLabel: { fontSize: '10px', color: c.axisLabel, fontFamily: 'JetBrains Mono,monospace' }, axisTick: { show: false }, axisLine: { show: false }, axisPointer: { show: true, lineStyle: { color: c.splitLine }, label: { shadowBlur: 0, backgroundColor: c.axisPointer, color: '#f7f8f9' } }, splitLine: { show: this.showGrid, lineStyle: { color: c.splitLine } } });
    if (!opts.dataZoom[0].xAxisIndex?.includes(gi)) { if (!Array.isArray(opts.dataZoom[0].xAxisIndex)) opts.dataZoom[0].xAxisIndex = [0]; opts.dataZoom[0].xAxisIndex.push(gi); }
    this._applyOptions(opts);
    await this._refreshGridSize();
    return gi;
  }

  async _addSubIndicatorSeries(data, color, thickness, title, gridIndex) {
    const opts = await this._getOptions(); if (!opts) return;
    const prefix = this._generateEmptyPrefix(data.length, this.lineData.length);
    const payload = prefix.concat(this._fmtInd(data));
    const { xi, yi } = this._getAxisIndices(opts, gridIndex);
    opts.series.push({
      name: title, type: 'line', xAxisIndex: xi === -1 ? 0 : xi, yAxisIndex: yi === -1 ? 0 : yi, symbolSize: 0, data: payload, smooth: false,
      lineStyle: { color, width: thickness }, itemStyle: { color }, areaStyle: { opacity: 0 }
    });
    this._applyOptions(opts);
  }

  async _addMarkLineToSeries(color, value, seriesIndex, thickness = 2, type = 'solid') {
    const opts = await this._getOptions();
    if (!opts?.series?.[seriesIndex]) return;
    if (!opts.series[seriesIndex].markLine) opts.series[seriesIndex].markLine = { symbol: ['none', 'none'], data: [] };
    opts.series[seriesIndex].markLine.data.push({ yAxis: value, symbol: 'circle', symbolSize: [0, 0], lineStyle: { color, width: thickness, type } });
    this._applyOptions(opts);
  }

  async _refreshGridSize() {
    const opts = await this._getOptions(); if (!opts) return;
    const visible = opts.grid.map((g, i) => ({ g, i }))
      .filter(({ g, i }) => i === 0 || (g && g.show !== false && g.top !== '200%'))
      .map(({ i }) => i);

    if (visible.length === 1) {
      opts.grid[0].top = '8px';
      opts.grid[0].bottom = '0';
      opts.grid[0].height = null; // Let it fill based on top/bottom
    } else if (visible.length === 2) {
      opts.grid[0].top = '8px'; opts.grid[0].height = '62%';
      opts.grid[0].bottom = null;
      const gi = visible[1];
      opts.grid[gi].top = '67%'; opts.grid[gi].height = '31%'; opts.grid[gi].show = true;
      opts.grid[gi].bottom = null;
    } else {
      opts.grid[0].top = '8px'; opts.grid[0].height = '50%';
      opts.grid[0].bottom = null;
      const sub = visible.slice(1); const h = Math.floor(47 / sub.length);
      sub.forEach((gi, k) => {
        opts.grid[gi].height = `${h - 2}%`;
        opts.grid[gi].top = `${53 + h * k}%`;
        opts.grid[gi].show = true;
        opts.grid[gi].bottom = null;
      });
    }

    // Ensure hidden grids are really hidden
    opts.grid.forEach((g, i) => {
      if (i > 0 && !visible.includes(i)) {
        g.show = false;
        g.top = '200%';
        g.height = '0%';
      }
    });

    this._applyOptions(opts);
    // Force a resize to ensure the canvas fills the area correctly
    setTimeout(() => { if (this._chartInstance) this._chartInstance.resize(); }, 100);
  }

  async _updateAllIndicators() {
    for (const [k, v] of Object.entries(this.chartIndicator ?? {})) {
      if (!v) continue; await sleep(1);
      const fn = { EMA: () => this._updateEMA(k), SMA: () => this._updateSMA(k), BBANDS: () => this._updateBBANDS(k), MACD: () => this._updateMACD(k), SO: () => this._updateSO(k), RSI: () => this._updateRSI(k), CCI: () => this._updateCCI(k), ROC: () => this._updateROC(k), ADX: () => this._updateADX(k) }[v.indicator];
      if (fn) await fn().catch(() => { });
    }
  }

  // EMA
  async addEMA({ indexIndicator, period = 14, colour = '#18dae6', thickness = 1, update = false } = {}) {
    indexIndicator ??= this._genId();
    const calc = EMA.calculate({ period, values: this.lineData });
    if (update) return calc;
    this.chartIndicator[indexIndicator] = { indicator: 'EMA', serieIndex: await this._getSeriesLength(), gridIndex: 0, parms: { period, colour, thickness } };
    await this._addIndicatorSeries(calc, colour, thickness, `EMA (${period})`);
    return { index: indexIndicator, title: `EMA (${period})` };
  }
  async changeEMA(id, { period, thickness, colour }) {
    const ind = this.chartIndicator[id]; if (!ind) return;
    Object.assign(ind.parms, { period: +period, thickness, colour });
    const opts = await this._getOptions(); if (!opts?.series?.[ind.serieIndex]) return;
    opts.series[ind.serieIndex].lineStyle = { color: colour, width: thickness };
    opts.series[ind.serieIndex].name = `EMA (${period})`;
    this._applyOptions(opts); await this._updateEMA(id);
  }
  async _updateEMA(id) {
    const ind = this.chartIndicator[id]; if (!ind) return;
    const opts = await this._getOptions(); if (!opts?.series?.[ind.serieIndex]) return;
    const calc = this._fmtInd(EMA.calculate({ period: ind.parms.period, values: this.lineData }));
    opts.series[ind.serieIndex].data = this._generateEmptyPrefix(calc.length, this.lineData.length).concat(calc);
    this._applyOptions(opts);
  }

  // SMA
  async addSMA({ indexIndicator, period = 14, colour = '#da4931', thickness = 1, update = false } = {}) {
    indexIndicator ??= this._genId();
    const calc = SMA.calculate({ period, values: this.lineData });
    if (update) return calc;
    this.chartIndicator[indexIndicator] = { indicator: 'SMA', serieIndex: await this._getSeriesLength(), gridIndex: 0, parms: { period, colour, thickness } };
    await this._addIndicatorSeries(calc, colour, thickness, `SMA (${period})`);
    return { index: indexIndicator, title: `SMA (${period})` };
  }
  async changeSMA(id, { period, thickness, colour }) {
    const ind = this.chartIndicator[id]; if (!ind) return;
    Object.assign(ind.parms, { period: +period, thickness, colour });
    const opts = await this._getOptions(); if (!opts?.series?.[ind.serieIndex]) return;
    opts.series[ind.serieIndex].lineStyle = { color: colour, width: thickness };
    this._applyOptions(opts); await this._updateSMA(id);
  }
  async _updateSMA(id) {
    const ind = this.chartIndicator[id]; if (!ind) return;
    const opts = await this._getOptions(); if (!opts?.series?.[ind.serieIndex]) return;
    const calc = this._fmtInd(SMA.calculate({ period: ind.parms.period, values: this.lineData }));
    opts.series[ind.serieIndex].data = this._generateEmptyPrefix(calc.length, this.lineData.length).concat(calc);
    this._applyOptions(opts);
  }

  // BBANDS
  async addBBANDS({ indexIndicator, period = 20, deviation = 2, uppercolor = '#5ff347', upperthickness = 1, middlecolor = '#18dae6', middlethickness = 1, lowercolor = '#c21b26', lowerthickness = 1, update = false } = {}) {
    indexIndicator ??= this._genId();
    const up = [], mid = [], low = [];
    BollingerBands.calculate({ period, values: this.lineData, stdDev: deviation })?.forEach(v => { up.push(v.upper); mid.push(v.middle); low.push(v.lower); });
    if (update) return { upper: up, middle: mid, lower: low };
    this.chartIndicator[indexIndicator] = { indicator: 'BBANDS', serieIndex: await this._getSeriesLength(), gridIndex: 0, parms: { period, deviation, uppercolor, upperthickness, middlecolor, middlethickness, lowercolor, lowerthickness } };
    await this._addIndicatorSeries(up, uppercolor, upperthickness, `BB Upper (${period})`);
    await this._addIndicatorSeries(mid, middlecolor, middlethickness, `BB Mid (${period})`);
    await this._addIndicatorSeries(low, lowercolor, lowerthickness, `BB Lower (${period})`);
    return { index: indexIndicator, title: `BBANDS (${period})` };
  }
  async changeBBANDS(id, p) {
    const ind = this.chartIndicator[id]; if (!ind) return;
    Object.assign(ind.parms, p); await this._updateBBANDS(id);
  }
  async _updateBBANDS(id) {
    const ind = this.chartIndicator[id]; if (!ind) return;
    const opts = await this._getOptions(); if (!opts?.series?.[ind.serieIndex + 2]) return;
    const { upper, middle, lower } = await this.addBBANDS({ ...ind.parms, update: true });
    [upper, middle, lower].forEach((arr, i) => { const f = this._fmtInd(arr); opts.series[ind.serieIndex + i].data = this._generateEmptyPrefix(f.length, this.lineData.length).concat(f); });
    this._applyOptions(opts);
  }

  // MACD
  async addMACD({ indexIndicator, fastperiod = 12, slowPeriod = 26, signalPeriod = 9, thickness = 1, macd_colour = '#5ff347', signal_colour = '#18dae6', update = false } = {}) {
    indexIndicator ??= this._genId();
    const mac = MACD.calculate({ fastPeriod: fastperiod, slowPeriod, signalPeriod, SimpleMAOscillator: false, SimpleMASignal: false, values: this.lineData });
    const macdVals = mac.map(m => m.MACD || 0), signalVals = mac.map(m => m.signal || 0);
    if (update) return { macd: macdVals, signal: signalVals };
    const gi = await this._addSubChart();
    const si = await this._getSeriesLength();
    this.chartIndicator[indexIndicator] = { indicator: 'MACD', serieIndex: si, gridIndex: gi, parms: { fastperiod, slowPeriod, signalPeriod, thickness, macd_colour, signal_colour } };
    await this._addSubIndicatorSeries(macdVals, macd_colour, thickness, `MACD`, gi);
    await this._addSubIndicatorSeries(signalVals, signal_colour, thickness, `Signal`, gi);
    return { index: indexIndicator, title: 'MACD' };
  }
  async changeMACD(id, p) { const ind = this.chartIndicator[id]; if (!ind) return; Object.assign(ind.parms, p); await this._updateMACD(id); }
  async _updateMACD(id) {
    const ind = this.chartIndicator[id]; if (!ind) return;
    const opts = await this._getOptions(); if (!opts?.series?.[ind.serieIndex + 1]) return;
    const { macd, signal } = await this.addMACD({ ...ind.parms, update: true });
    [macd, signal].forEach((arr, i) => { const f = this._fmtInd(arr); opts.series[ind.serieIndex + i].data = this._generateEmptyPrefix(f.length, this.lineData.length).concat(f); });
    this._applyOptions(opts);
  }

  // RSI
  async addRSI({ indexIndicator, period = 14, color = '#18dae6', thickness = 1, over = 70, over_color = '#5ff347', under = 30, under_color = '#c21b26', update = false } = {}) {
    indexIndicator ??= this._genId();
    const calc = RSI.calculate({ period, values: this.lineData });
    if (update) return calc;
    const gi = await this._addSubChart();
    const si = await this._getSeriesLength();
    this.chartIndicator[indexIndicator] = { indicator: 'RSI', serieIndex: si, gridIndex: gi, parms: { period, color, thickness, over, over_color, under, under_color } };
    await this._addSubIndicatorSeries(calc, color, thickness, `RSI (${period})`, gi);
    await this._addMarkLineToSeries(over_color, over, si, 1, 'dashed');
    await this._addMarkLineToSeries(under_color, under, si, 1, 'dashed');
    return { index: indexIndicator, title: `RSI (${period})` };
  }
  async changeRSI(id, p) { const ind = this.chartIndicator[id]; if (!ind) return; Object.assign(ind.parms, p); await this._updateRSI(id); }
  async _updateRSI(id) {
    const ind = this.chartIndicator[id]; if (!ind) return;
    const opts = await this._getOptions(); if (!opts?.series?.[ind.serieIndex]) return;
    const calc = this._fmtInd(RSI.calculate({ period: ind.parms.period, values: this.lineData }));
    opts.series[ind.serieIndex].data = this._generateEmptyPrefix(calc.length, this.lineData.length).concat(calc);
    this._applyOptions(opts);
  }

  // CCI
  async addCCI({ indexIndicator, period = 14, color = '#5ff347', thickness = 1, color_trend = '#eda129', update = false } = {}) {
    indexIndicator ??= this._genId();
    const highs = this.candlestickData.map(c => c[3]), lows = this.candlestickData.map(c => c[2]), closes = this.lineData;
    const calc = CCI.calculate({ period, high: highs, low: lows, close: closes });
    if (update) return calc;
    const gi = await this._addSubChart();
    const si = await this._getSeriesLength();
    this.chartIndicator[indexIndicator] = { indicator: 'CCI', serieIndex: si, gridIndex: gi, parms: { period, color, thickness, color_trend } };
    await this._addSubIndicatorSeries(calc, color, thickness, `CCI (${period})`, gi);
    await this._addMarkLineToSeries(color_trend, 100, si, 1, 'dashed');
    await this._addMarkLineToSeries(color_trend, -100, si, 1, 'dashed');
    return { index: indexIndicator, title: `CCI (${period})` };
  }
  async changeCCI(id, p) { const ind = this.chartIndicator[id]; if (!ind) return; Object.assign(ind.parms, p); await this._updateCCI(id); }
  async _updateCCI(id) {
    const ind = this.chartIndicator[id]; if (!ind) return;
    const opts = await this._getOptions(); if (!opts?.series?.[ind.serieIndex]) return;
    const h = this.candlestickData.map(c => c[3]), l = this.candlestickData.map(c => c[2]);
    const calc = this._fmtInd(CCI.calculate({ period: ind.parms.period, high: h, low: l, close: this.lineData }));
    opts.series[ind.serieIndex].data = this._generateEmptyPrefix(calc.length, this.lineData.length).concat(calc);
    this._applyOptions(opts);
  }

  // SO (Stochastic)
  async addSO({ indexIndicator, kPeriod = 14, dPeriod = 3, thickness = 1, overbuy = 80, overbuy_color = '#5ff347', oversell = 20, oversell_color = '#c21b26', kseries_color = '#d0e521', dseries_color = '#18dae6', update = false } = {}) {
    indexIndicator ??= this._genId();
    const highs = this.candlestickData.map(c => c[3]), lows = this.candlestickData.map(c => c[2]), closes = this.lineData;
    const so = Stochastic.calculate({ high: highs, low: lows, close: closes, period: kPeriod, signalPeriod: dPeriod });
    const kVals = so.map(s => s.k || 0), dVals = so.map(s => s.d || 0);
    if (update) return { k: kVals, d: dVals };
    const gi = await this._addSubChart();
    const si = await this._getSeriesLength();
    this.chartIndicator[indexIndicator] = { indicator: 'SO', serieIndex: si, gridIndex: gi, parms: { kPeriod, dPeriod, thickness, overbuy, overbuy_color, oversell, oversell_color, kseries_color, dseries_color } };
    await this._addSubIndicatorSeries(kVals, kseries_color, thickness, '%K', gi);
    await this._addSubIndicatorSeries(dVals, dseries_color, thickness, '%D', gi);
    await this._addMarkLineToSeries(overbuy_color, overbuy, si, 1, 'dashed');
    await this._addMarkLineToSeries(oversell_color, oversell, si, 1, 'dashed');
    return { index: indexIndicator, title: 'KDJ' };
  }
  async changeSO(id, p) { const ind = this.chartIndicator[id]; if (!ind) return; Object.assign(ind.parms, p); await this._updateSO(id); }
  async _updateSO(id) {
    const ind = this.chartIndicator[id]; if (!ind) return;
    const opts = await this._getOptions(); if (!opts?.series?.[ind.serieIndex + 1]) return;
    // const h=this.candlestickData.map(c=>c[3]),l=this.candlestickData.map(c=>c[2]);
    const { k, d } = await this.addSO({ ...ind.parms, update: true });
    [k, d].forEach((arr, i) => { const f = this._fmtInd(arr); opts.series[ind.serieIndex + i].data = this._generateEmptyPrefix(f.length, this.lineData.length).concat(f); });
    this._applyOptions(opts);
  }

  // ROC
  async addROC({ indexIndicator, period = 12, color = '#5ff347', thickness = 1, update = false } = {}) {
    indexIndicator ??= this._genId();
    const calc = ROC.calculate({ period, values: this.lineData });
    if (update) return calc;
    const gi = await this._addSubChart();
    const si = await this._getSeriesLength();
    this.chartIndicator[indexIndicator] = { indicator: 'ROC', serieIndex: si, gridIndex: gi, parms: { period, color, thickness } };
    await this._addSubIndicatorSeries(calc, color, thickness, `ROC (${period})`, gi);
    return { index: indexIndicator, title: `ROC (${period})` };
  }
  async changeROC(id, p) { const ind = this.chartIndicator[id]; if (!ind) return; Object.assign(ind.parms, p); await this._updateROC(id); }
  async _updateROC(id) {
    const ind = this.chartIndicator[id]; if (!ind) return;
    const opts = await this._getOptions(); if (!opts?.series?.[ind.serieIndex]) return;
    const calc = this._fmtInd(ROC.calculate({ period: ind.parms.period, values: this.lineData }));
    opts.series[ind.serieIndex].data = this._generateEmptyPrefix(calc.length, this.lineData.length).concat(calc);
    this._applyOptions(opts);
  }

  // ADX
  async addADX({ indexIndicator, period = 14, thickness = 1, adxcolor = '#5ff347', mdicolor = '#da4931', pdicolor = '#c21b26', update = false } = {}) {
    indexIndicator ??= this._genId();
    const highs = this.candlestickData.map(c => c[3]), lows = this.candlestickData.map(c => c[2]), closes = this.lineData;
    const adxRes = ADX.calculate({ period, close: closes, high: highs, low: lows });
    const adxV = adxRes.map(a => a.adx || 0), pdiV = adxRes.map(a => a.pdi || 0), mdiV = adxRes.map(a => a.mdi || 0);
    if (update) return { adx: adxV, pdi: pdiV, mdi: mdiV };
    const gi = await this._addSubChart();
    const si = await this._getSeriesLength();
    this.chartIndicator[indexIndicator] = { indicator: 'ADX', serieIndex: si, gridIndex: gi, parms: { period, thickness, adxcolor, mdicolor, pdicolor } };
    await this._addSubIndicatorSeries(adxV, adxcolor, thickness, 'ADX', gi);
    await this._addSubIndicatorSeries(pdiV, pdicolor, thickness, '+DI', gi);
    await this._addSubIndicatorSeries(mdiV, mdicolor, thickness, '-DI', gi);
    return { index: indexIndicator, title: `ADX (${period})` };
  }
  async changeADX(id, p) { const ind = this.chartIndicator[id]; if (!ind) return; Object.assign(ind.parms, p); await this._updateADX(id); }
  async _updateADX(id) {
    const ind = this.chartIndicator[id]; if (!ind) return;
    const opts = await this._getOptions(); if (!opts?.series?.[ind.serieIndex + 2]) return;
    // const h=this.candlestickData.map(c=>c[3]),l=this.candlestickData.map(c=>c[2]);
    const { adx, pdi, mdi } = await this.addADX({ ...ind.parms, update: true });
    [adx, pdi, mdi].forEach((arr, i) => { const f = this._fmtInd(arr); opts.series[ind.serieIndex + i].data = this._generateEmptyPrefix(f.length, this.lineData.length).concat(f); });
    this._applyOptions(opts);
  }

  async removeIndicator(indexIndicator, typeIndicator, forcehide = true) {
    const dataIndicator = this.chartIndicator[indexIndicator]; if (!dataIndicator) return;
    const nSeries = { BBANDS: 3, MACD: 2, ADX: 3, SO: 2 }[typeIndicator] ?? 1;
    const opts = await this._getOptions(); if (!opts?.series?.[dataIndicator.serieIndex]) return;
    for (let i = dataIndicator.serieIndex; i < dataIndicator.serieIndex + nSeries; i++) {
      if (!opts.series[i]) continue;
      if (forcehide) {
        opts.series[i].lineStyle = { ...(opts.series[i].lineStyle ?? {}), opacity: 0 };
        opts.series[i].itemStyle = { ...(opts.series[i].itemStyle ?? {}), opacity: 0 };
      } else {
        const op = opts.series[i].lineStyle?.opacity ?? 1;
        opts.series[i].lineStyle = { ...(opts.series[i].lineStyle ?? {}), opacity: op === 0 ? 1 : 0 };
      }
    }
    if (forcehide && dataIndicator.gridIndex > 0) {
      const gi = dataIndicator.gridIndex;
      const { xi, yi } = this._getAxisIndices(opts, gi);
      if (opts.grid[gi]) { opts.grid[gi].show = false; opts.grid[gi].height = '0%'; opts.grid[gi].top = '200%'; }
      if (xi !== -1) opts.xAxis[xi].show = false;
      if (yi !== -1) opts.yAxis[yi].show = false;
    }
    if (forcehide) this.chartIndicator[indexIndicator] = null;
    this._applyOptions(opts);
    await sleep(50); await this._refreshGridSize();
  }

  _handleDataZoom(params) {
    if (!this.chart_ready || this._loadingOlderHistory) return;
    const opts = this._chartInstance?.getOption();
    const dz = opts?.dataZoom?.[0];
    if (!dz) return;
    if (dz.start <= 5) {
      this._fetchOlderHistory();
    }
  }

  async _fetchOlderHistory() {
    if (this._loadingOlderHistory || !this.chart_ready || !this.timestamps.length) return;
    this._loadingOlderHistory = true;

    try {
      const oldestTs = this.timestamps[0];
      const ms = getIntervalMs(this.interval);
      const to = oldestTs - 1;
      const from = to - (TARGET_CANDLES * ms);

      const state = store.getState();
      const user = state.platform.user;
      const group = user?.group_id?.group_name || '';

      console.log(`[ItcChartService] Fetching older history: from ${new Date(from).toISOString()} to ${new Date(to).toISOString()}`);

      const res = await platformService.getChartHistory(
        this.symbol,
        this.interval,
        from,
        to,
        group
      );

      const candles = this._extractHistoryCandles(res);
      if (candles.length > 0) {
        this._prependHistory(candles);
      }
    } catch (err) {
      console.error('[ItcChartService] _fetchOlderHistory error:', err);
    } finally {
      this._loadingOlderHistory = false;
    }
  }

  _prependHistory(data) {
    if (!data.length) return;

    const newDates = [];
    const newCandlestick = [];
    const newLine = [];
    const newTimestamps = [];

    const oldestLoadedTs = this.timestamps[0];

    for (const item of data) {
      const candle = this._normaliseCandle(item);
      if (!candle || candle.timestamp >= oldestLoadedTs) continue;

      newDates.push(this._formatDate(candle.timestamp));
      newCandlestick.push([candle.open, candle.close, candle.low, candle.high]);
      newLine.push(candle.close);
      newTimestamps.push(candle.timestamp);
    }

    if (newDates.length === 0) return;

    const currentOpts = this._chartInstance?.getOption();
    const oldZoom = currentOpts?.dataZoom?.[0];
    const oldTotal = this.dates.length;

    this.dates = [...newDates, ...this.dates];
    this.candlestickData = [...newCandlestick, ...this.candlestickData];
    this.lineData = [...newLine, ...this.lineData];
    this.timestamps = [...newTimestamps, ...this.timestamps];

    const oldStartPerc = oldZoom ? oldZoom.start : 0;
    const oldEndPerc = oldZoom ? oldZoom.end : 100;

    const oldStartIdx = Math.round((oldStartPerc / 100) * oldTotal);
    const oldEndIdx = Math.round((oldEndPerc / 100) * oldTotal);

    const prependedCount = newDates.length;
    const newTotal = this.dates.length;

    const newStartPerc = ((oldStartIdx + prependedCount) / newTotal) * 100;
    const newEndPerc = ((oldEndIdx + prependedCount) / newTotal) * 100;

    this.zoomLevel = newStartPerc;

    const c = this._getColors();
    const isCandle = this.chartType === 'candlestick';
    const isArea = this.chartType === 'area';
    const datesWithGap = this._datesWithGap();

    const upd = {
      xAxis: Array.from({ length: currentOpts?.xAxis?.length || 1 }, () => ({ data: datesWithGap })),
      dataZoom: [{
        start: newStartPerc,
        end: newEndPerc
      }],
      series: [{
        data: this.candlestickData,
        itemStyle: { color: c.buyCandle, color0: c.sellCandle, borderColor: c.buyCandle, borderColor0: c.sellCandle, opacity: isCandle ? 1 : 0 },
        markPoint: { data: this._buildMarkPointData() },
        markLine: { data: this._buildMarkLineData() },
      }, {
        data: this.lineData,
        lineStyle: { color: c.lineChart, opacity: isCandle ? 0 : 1 },
        areaStyle: { opacity: isArea ? 1 : 0, origin: 'start', color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: c.lineChartA1 }, { offset: 1, color: c.lineChartA2 }], global: false } },
      }],
    };

    this._applyOptions(upd);
    this._updateAllIndicators().catch(() => { });
  }

  setTheme(mode = 'dark') { if (this.chart_ready) this._forceRedraw(); }
  setThemeColors(colors) {
    this.themeColors = colors;
    if (this.chart_ready) this._forceRedraw();
  }
  setChartThemeMode(mode = 'dark') { this.setTheme(mode); }
  dispose() { this._chartInstance?.dispose(); this._chartInstance = null; }
}

export default ItcChartService;
